import React, { useState } from 'react';

type SmartImageProps = {
  basePath: string; // without extension, e.g., /images/exclaim-jersey
  alt: string;
  className?: string;
  fallback: React.ReactNode;
};

const SmartImage: React.FC<SmartImageProps> = ({ basePath, alt, className, fallback }) => {
  const candidates = [
    `${basePath}.webp`,
    `${basePath}.png`,
    `${basePath}.jpg`,
    `${basePath}.jpeg`,
  ];
  const [candidateIndex, setCandidateIndex] = useState(0);

  if (candidateIndex >= candidates.length) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={candidates[candidateIndex]}
      alt={alt}
      className={className}
      onError={() => setCandidateIndex(i => i + 1)}
    />
  );
};

const Merch: React.FC = () => {
  const handleExclaimJersey = () => {
    window.open('https://exclaim.gg/store/just-vibes', '_blank', 'noopener,noreferrer');
  };

  const handleRavenJersey = () => {
    window.open('https://raven.gg/create/build/?share=688a3c8facac3', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
          <span className="gradient-text">Guild Merch</span>
        </h1>
        <p className="text-xl text-text-secondary mb-8 text-center max-w-2xl mx-auto">
          Want to show your guild pride with some Just Vibes merch? We have two jersey styles for you to order and customize!
        </p>

                                   {/* Jersey Sections */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
             {/* Exclaim Jersey */}
             <div className="bg-black rounded-lg p-6 border border-gray-600 hover:border-pink-400 hover:shadow-lg hover:shadow-pink-400/20 transition-all duration-300">
                         <h2 className="text-2xl font-bold text-cyan-400 mb-4 text-center">Exclaim Jersey</h2>
             
             {/* Jersey Image with multi-format support */}
             <div className="mb-4">
              <SmartImage
                basePath="/images/exclaim-jersey"
                alt="Exclaim Jersey - Front and Back Views"
                className="w-full rounded-lg"
                fallback={
                  <div className="w-full h-64 bg-gray-700 rounded-lg flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">⚾</div>
                      <div>Exclaim Jersey Preview</div>
                      <div className="text-sm mt-2">Black with pink sleeves and teal piping</div>
                    </div>
                  </div>
                }
              />
            </div>

                         <p className="text-gray-300 mb-4 text-center">
               Customize your Just Vibes jersey with our official design on Exclaim's platform.
             </p>

             {/* Instructions */}
             <div className="mb-4">
              <h4 className="font-bold text-gray-200 mb-3">Customization Instructions:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Click the link below to open the jersey builder</li>
                <li>
                  <span className="font-bold text-cyan-400">Important:</span> Customize the "Name" field with your character name before adding to cart
                </li>
                <li>Select your preferred size and quantity</li>
                <li>Add to cart and complete your order</li>
              </ol>
            </div>

            <button
              onClick={handleExclaimJersey}
              className="w-full bg-transparent border-2 border-cyan-400 text-white py-3 px-6 rounded-lg hover:bg-cyan-400 hover:text-black transition-all duration-300 font-semibold"
            >
              Customize Exclaim Jersey
            </button>
          </div>

                                          {/* Raven Jersey */}
            <div className="bg-black rounded-lg p-6 border border-gray-600 hover:border-pink-400 hover:shadow-lg hover:shadow-pink-400/20 transition-all duration-300">
                         <h2 className="text-2xl font-bold text-cyan-400 mb-4 text-center">Raven Jersey</h2>
             
             {/* Jersey Image with multi-format support */}
             <div className="mb-4">
              <SmartImage
                basePath="/images/raven-jersey"
                alt="Raven Jersey - Front and Back Views"
                className="w-full rounded-lg"
                fallback={
                  <div className="w-full h-64 bg-gray-700 rounded-lg flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🏈</div>
                      <div>Raven Jersey Preview</div>
                      <div className="text-sm mt-2">Black with teal and pink geometric design</div>
                    </div>
                  </div>
                }
              />
            </div>

                         <p className="text-gray-300 mb-4 text-center">
               Design your Just Vibes jersey with our official design on Raven's platform.
             </p>

             {/* Instructions */}
             <div className="mb-4">
              <h4 className="font-bold text-gray-200 mb-3">Customization Instructions:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Click the link below to open the jersey builder</li>
                <li>
                  <span className="font-bold text-cyan-400">Important:</span> Customize the "Name" field with your character name before adding to cart
                </li>
                <li>Select your preferred size and quantity</li>
                <li>Add to cart and complete your order</li>
              </ol>
            </div>

            <button
              onClick={handleRavenJersey}
              className="w-full bg-transparent border-2 border-cyan-400 text-white py-3 px-6 rounded-lg hover:bg-cyan-400 hover:text-black transition-all duration-300 font-semibold"
            >
              Customize Raven Jersey
            </button>
                     </div>
         </div>

                   {/* Important Notes Section */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-black rounded-lg p-6 border border-cyan-400">
             <h3 className="text-xl font-bold text-cyan-400 mb-4">Important Notes:</h3>
             <ul className="space-y-2 text-gray-300">
               <li className="flex items-start">
                 <span className="text-cyan-400 mr-2 mt-1">•</span>
                 <span>Both jersey styles feature our official Just Vibes design</span>
               </li>
               <li className="flex items-start">
                 <span className="text-cyan-400 mr-2 mt-1">•</span>
                 <span>Make sure to customize the "Name" field with your character name before purchasing</span>
               </li>
               <li className="flex items-start">
                 <span className="text-cyan-400 mr-2 mt-1">•</span>
                 <span>Orders are fulfilled directly by Exclaim and Raven - we don't handle the merchandise</span>
               </li>
               <li className="flex items-start">
                 <span className="text-cyan-400 mr-2 mt-1">•</span>
                 <span>Shipping times and costs vary by platform and location</span>
               </li>
               <li className="flex items-start">
                 <span className="text-cyan-400 mr-2 mt-1">•</span>
                 <span>For any issues with your order, please contact the respective platform's customer service</span>
               </li>
             </ul>
           </div>
         </div>
       </div>
     </div>
   );
 };

export default Merch;
